var _a;
import React, { forwardRef, Fragment, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styled from '@emotion/styled';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import Breakpoints from '@polestar/component-warehouse-react/tokens/breakpoints/Breakpoints';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Spinner, { SpinnerSizes, SpinnerTypes, } from '@polestar/component-warehouse-react/components/spinner/Spinner';
import Paragraph from '@polestar/component-warehouse-react/components/paragraph/Paragraph';
import { fullscreenVideoElementSupport, isSafariBrowser, } from '@polestar/component-warehouse-react/components/video-player/VideoPlayer.helpers';
import Icon, { IconSizes } from '@polestar/component-warehouse-react/components/icon/Icon';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import VideoPlayerSkeleton from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerSkeleton';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import LineHeights from '@polestar/component-warehouse-react/tokens/line-heights/LineHeights';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import VideoPlayerBufferSkeleton from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerBufferSkeleton';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useDimensionsCallback from '@polestar/component-warehouse-react/hooks/useDimensions';
import VideoPlayerControls from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerControls';
import { useVideoPlayer, VideoPlayerProvider, } from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerContext';
import { useUpdateEffect } from '@polestar/component-warehouse-react/hooks/useUpdateEffect';
import { useContainerFocusTriggers } from '@polestar/component-warehouse-react/hooks/useContainerFocusTriggers';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
import { fullscreenElementSupport } from '@polestar/component-warehouse-react/helpers/fullscreen';
import { isBrowser } from '@polestar/component-warehouse-react/helpers/supports';
import VideoPlayerSubtitles from '@polestar/component-warehouse-react/components/video-player/internal/VideoPlayerSubtitles';
import { useVideoPlayerControls, } from '@polestar/component-warehouse-react/components/video-player/useVideoPlayerControls';
import { focusProviderOptions, FullscreenFocusRing, } from '@polestar/component-warehouse-react/contexts/focus';
import { useSSRSafeId } from '@polestar/component-warehouse-react/contexts/ssr';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { VideoPlayerActionButtonContext, } from '@polestar/component-warehouse-react/components/video-player/VideoPlayerActionButton';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
import { preventCancelableDefault } from '@polestar/component-warehouse-react/helpers/event';
// We do not want to trigger mouse over controls handlers on touch devices
const doMouseOverAction = !!(isBrowser && ((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: fine)').matches));
const VideoPlayer = forwardRef((props, ref) => {
    const isMuted = props.muted;
    const _controls = useVideoPlayerControls();
    const [muted, setMuted] = useState(isMuted);
    useEffect(() => {
        // ensure changes are listened to and can be used inside the context
        setMuted(isMuted);
    }, [isMuted]);
    return (React.createElement(VideoPlayerProvider, { muted: muted, autoPlay: props.autoPlay, controls: props.videoPlayerControls || _controls },
        React.createElement(VideoPlayerWithoutContext, { ref: ref, ...props })));
});
VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;
const VideoPlayerWithoutContext = forwardRef(({ children, title, controls = true, subtitleLabel = 'Subtitles', subtitleOffLabel = 'Off', backButtonLabel = 'Back to menu', ariaLabels = {
    play: 'Play',
    pause: 'Pause',
    fullscreen: 'Go fullscreen',
    exitFullscreen: 'Exit fullscreen',
    mute: 'Mute',
    unmute: 'Unmute',
    replay: 'Replay',
    menu: 'Show menu',
    progress: 'Progress',
}, menu, endScreenContent, endScreenContentFullCanvas = false, actions = [], onFocus, onBlur, onKeyDown, onControlsChange, onReplay, onFullscreenChange, onSubtitleChange, onClick, onTouchEnd, ignoreReducedMotion = false, disableLowPowerModePlayButton = false, containerProps, ...props }, ref) => {
    var _a, _b, _c, _d, _e;
    if ((ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.play) === undefined) {
        ariaLabels.play = 'Play';
    }
    /**
     * state
     */
    const { isSafari } = isSafariBrowser();
    const isAutoPlay = (disableLowPowerModePlayButton && !isSafari) || !!props.autoPlay;
    const isReducedMotion = useReducedMotion();
    const { isKeyboardControlled } = useInteractionMode();
    const [showControls, setShowControls] = useState(false);
    const [mouseOverControls, setMouseOverControls] = useState(false);
    const hideControlsTimer = useRef(null);
    const memoizedId = useSSRSafeId('video-player', props.id);
    if (disableLowPowerModePlayButton && props.autoPlay) {
        warnOnce('autoplay', '[VideoPlayer] do not use `autoPlay` with `disableLowPowerModePlayButton`');
    }
    const { playerRef, videoPlayerRef, menuOpen, fullscreen, playing, togglePlaying, canPlay, suspended, hasBeenSuspended, buffering, selectedSubtitle, ended, startedPlaying, nativeVideoPlayerVisible, } = useVideoPlayer();
    const [nativeControlsVisible, setNativeControlsVisible] = useState(false);
    // needed to determine for example when the opening screen is needed
    // also used to determine whether to render the skeleton playbutton for the video
    const showBigPlayButton = !disableLowPowerModePlayButton &&
        ((!playing && !startedPlaying && !isAutoPlay) ||
            (!playing && suspended && !isAutoPlay) ||
            (!playing && isAutoPlay && isReducedMotion));
    const restartHideControlsTimer = useCallback(() => {
        if (hideControlsTimer.current) {
            clearTimeout(hideControlsTimer.current);
        }
        setShowControls(true);
        hideControlsTimer.current = setTimeout(() => {
            setShowControls(false);
        }, 4000);
    }, []);
    const handleFocus = useCallback(() => {
        restartHideControlsTimer();
        onFocus === null || onFocus === void 0 ? void 0 : onFocus();
    }, [onFocus, restartHideControlsTimer]);
    const handleBlur = useCallback(() => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur();
        if (!fullscreenElementSupport) {
            restartHideControlsTimer();
        }
    }, [onBlur, restartHideControlsTimer]);
    useEffect(() => {
        if (!showControls && hideControlsTimer.current) {
            clearTimeout(hideControlsTimer.current);
        }
    }, [showControls]);
    const { focusBlurEvents, focused } = useContainerFocusTriggers({
        name: `video-player-${memoizedId}`,
        fakeFocus: menuOpen || fullscreen,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onPointerUp: props.onPointerUp,
        onPointerDown: props.onPointerDown,
    });
    /**
     * controls visible logic
     *
     * Interaction mode: Mouse
     * Show:
     *  - on mouse movement over video
     *  - OR on video started playing (big play button / overlay is clicked)
     *  - OR video is ended
     *
     * Hide:
     *  - on no mouse movement for 4 seconds
     *    - AND mouse is not on the controls bar
     *    - AND context menu is not open
     *
     * Interaction mode: Keyboard
     * Show:
     *  - on focus
     *  - OR on play / pause (spacebar is pressed)
     *  - OR on video start (big play button / overlay is clicked)
     *  - OR video is ended
     *
     *  Hide:
     *  - when ESC is pressed (not fullscreen)
     *
     * Interaction mode: Touch
     *  - Does not apply, native iOS / android logic is applied
     */
    const forceControlsOpen = controls && !nativeControlsVisible && (ended || menuOpen);
    const controlsBasicRequirements = !nativeControlsVisible && !showBigPlayButton && controls;
    const controlsKeyboardRequirements = focused && isKeyboardControlled;
    const controlsPointerRequirement = mouseOverControls;
    const controlsVisible = forceControlsOpen ||
        (controlsBasicRequirements &&
            (showControls || controlsKeyboardRequirements || controlsPointerRequirement));
    // Define when the action buttons is/are visible
    const actionsVisible = (canPlay && !playing && !startedPlaying) ||
        (!startedPlaying && playing) ||
        (canPlay && controlsVisible && startedPlaying);
    // Only animate actions when the big play button is pressed to start play
    const animateActions = startedPlaying;
    const combinedRef = useCombinedRef(ref, videoPlayerRef);
    useEffect(() => {
        if (!videoPlayerRef.current)
            return;
        let timeout;
        if (isSafari && disableLowPowerModePlayButton && videoPlayerRef) {
            timeout = setTimeout(() => {
                var _a;
                (_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.play().catch(() => {
                    // .catch() Necessary to prevent runtime errors in Safari.
                    console.warn('[VideoPlayer] Mobile Safari "Low Power Mode" detected.');
                });
            }, 0);
        }
        return () => {
            clearTimeout(timeout);
        };
    }, [videoPlayerRef, disableLowPowerModePlayButton, isSafari]);
    /**
     * Play pause
     */
    const handleTogglePlay = useCallback((e) => {
        var _a;
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
        togglePlaying();
        (_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, [togglePlaying, onClick, videoPlayerRef]);
    useEffect(() => {
        if (videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) {
            if (ended) {
                onReplay === null || onReplay === void 0 ? void 0 : onReplay();
            }
        }
    }, [onReplay, ended, videoPlayerRef]);
    /**
     * Progress
     */
    const hasEndScreen = endScreenContent !== undefined;
    useEffect(() => {
        const videoPlayer = videoPlayerRef.current;
        // show poster + play button when no end screen is provided
        if (videoPlayer && ended && !props.autoPlay && !props.loop && !hasEndScreen) {
            videoPlayer.load();
        }
    }, [ended, videoPlayerRef, props.loop, hasEndScreen, props.autoPlay]);
    /**
     * Size change
     */
    const [dimensionCallback, { width, height }] = useDimensionsCallback();
    const combinedPlayerRef = useCombinedRef(playerRef, dimensionCallback);
    const videoPlayerDimensions = {
        width,
        height,
        video: {
            width: ((_a = videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.videoWidth) || 0,
            height: ((_b = videoPlayerRef.current) === null || _b === void 0 ? void 0 : _b.videoHeight) || 0,
        },
    };
    const sizeVariant = useMemo(() => {
        if (width >= Breakpoints.tablet && width < Breakpoints.desktop) {
            return DeviceTypes.tablet;
        }
        else if (width >= Breakpoints.desktop) {
            return DeviceTypes.desktop;
        }
        else {
            return DeviceTypes.mobile;
        }
    }, [width]);
    /**
     * Fullscreen
     */
    useUpdateEffect(() => {
        onFullscreenChange === null || onFullscreenChange === void 0 ? void 0 : onFullscreenChange(fullscreen, videoPlayerRef);
    }, [onFullscreenChange, fullscreen, videoPlayerRef]);
    /**
     * Controls
     */
    useUpdateEffect(() => {
        onControlsChange === null || onControlsChange === void 0 ? void 0 : onControlsChange(controlsVisible);
    }, [onControlsChange, controlsVisible]);
    // Hide custom controls when native controls are visible
    useEffect(() => {
        if ((videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) && typeof MutationObserver !== 'undefined') {
            const observer = new MutationObserver((mutationsList) => {
                var _a, _b;
                for (const mutation of mutationsList) {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'controls') {
                        if (fullscreenVideoElementSupport && (videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current)) {
                            setNativeControlsVisible((_a = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _a === void 0 ? void 0 : _a.controls);
                        }
                        else if ((_b = videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) === null || _b === void 0 ? void 0 : _b.controls) {
                            // is needed because IOS will show the native controls when coming out of fullscreen mode
                            videoPlayerRef.current.controls = false;
                        }
                    }
                }
            });
            observer.observe(videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current, { attributes: true });
            return () => observer.disconnect();
        }
    }, [setNativeControlsVisible, videoPlayerRef]);
    const handleMouseEnterControls = useCallback(() => {
        if (doMouseOverAction) {
            setMouseOverControls(true);
        }
    }, []);
    const handleMouseLeaveControls = useCallback(() => {
        if (doMouseOverAction) {
            setMouseOverControls(false);
        }
    }, []);
    /**
     * We stop the "click" event on mobile, so it doesn't do a play/pause,
     */
    const handleTouchEnd = useCallback((e) => {
        onTouchEnd === null || onTouchEnd === void 0 ? void 0 : onTouchEnd(e);
        if (!fullscreen) {
            preventCancelableDefault(e);
        }
        restartHideControlsTimer();
    }, [onTouchEnd, fullscreen, restartHideControlsTimer]);
    const handleKeyDown = useCallback((e) => {
        switch (e.key) {
            case 'Escape':
                if (!fullscreen) {
                    setShowControls(false);
                }
                break;
            case ' ':
                if (videoPlayerRef.current === e.target) {
                    togglePlaying();
                }
                break;
        }
        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
    }, [onKeyDown, fullscreen, videoPlayerRef, togglePlaying]);
    /**
     * Subtitles
     */
    useUpdateEffect(() => {
        if (videoPlayerRef === null || videoPlayerRef === void 0 ? void 0 : videoPlayerRef.current) {
            onSubtitleChange === null || onSubtitleChange === void 0 ? void 0 : onSubtitleChange(videoPlayerRef.current.textTracks[selectedSubtitle]);
        }
    }, [onSubtitleChange, selectedSubtitle, videoPlayerRef]);
    const showTitle = !startedPlaying || showControls || (endScreenContent && ended);
    return (React.createElement(VideoPlayerContainer, { onMouseMove: restartHideControlsTimer, onMouseEnter: restartHideControlsTimer, onMouseLeave: restartHideControlsTimer, onKeyDown: handleKeyDown, ref: combinedPlayerRef, showCursor: controlsVisible || !(startedPlaying || canPlay) || (!controls && !controlsVisible), canPlay: canPlay, fullscreen: fullscreen, ...(canPlay ? { 'data-ready': true } : {}), ...containerProps, ...focusBlurEvents, nativeVideoPlayerVisible: nativeVideoPlayerVisible },
        React.createElement(FullscreenFocusRing, null),
        React.createElement(StyledVideo, { preload: "auto", ...props, 
            // touch devices cannot play by clicking on the video due to the Polestar HMI
            tabIndex: controls && !controlsVisible && startedPlaying && fullscreenElementSupport ? 0 : -1, controls: false, autoPlay: isAutoPlay && (!isReducedMotion || ignoreReducedMotion), ref: combinedRef, hasBeenSuspended: hasBeenSuspended, onClick: controls && controlsVisible ? handleTogglePlay : undefined, onTouchEnd: handleTouchEnd }, children),
        (startedPlaying || canPlay) && (props === null || props === void 0 ? void 0 : props.preload) !== 'none' && (React.createElement(VideoPlayerBufferSkeleton, { buffering: playing && !ended && buffering, playing: playing })),
        !canPlay && buffering && !startedPlaying && (props === null || props === void 0 ? void 0 : props.preload) !== 'none' && (React.createElement(VideoPlayerSkeleton, { width: "100%", height: "100%" }, showBigPlayButton && (React.createElement(ClickToPlay, null,
            React.createElement(BigPlayButton, { as: "div", skeleton: true, "data-type": "skeleton" }),
            React.createElement(PlayIcon, { icon: Icons.playFilled, skeleton: true, size: IconSizes.xLarge, topOffset: false }))))),
        endScreenContent && ended && (React.createElement(VideoPlayerEndSlot, { emptyCanvas: endScreenContentFullCanvas }, endScreenContent)),
        title && !(ended && endScreenContentFullCanvas) && (React.createElement(TitleContainer, { sizeVariant: sizeVariant },
            React.createElement(TitleBackground, { hide: !showTitle }),
            React.createElement(TitleContent, null,
                React.createElement(VideoPlayerTitle, { lineClamp: 1, visible: showTitle || false }, title),
                React.createElement(ActionButtonContainer, { visible: actionsVisible, animate: animateActions },
                    React.createElement(VideoPlayerActionButtonContext.Provider, { value: { transparent: !showBigPlayButton } }, actions.map((item, index) => (React.createElement(Fragment, { key: index }, item)))))))),
        (canPlay || (props === null || props === void 0 ? void 0 : props.preload) === 'none') && (React.createElement(Fragment, null,
            showBigPlayButton && (React.createElement(ClickToPlay, { onClick: handleTogglePlay },
                React.createElement(BigPlayButton, { type: "button", "data-type": "playbutton", ...aria({
                        label: (ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.bigPlay) || (ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.play),
                    }), title: (ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.bigPlay) || (ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels.play), ...focusProviderOptions({ round: true }) }),
                React.createElement(PlayIcon, { icon: Icons.playFilled, size: IconSizes.xLarge, topOffset: false }))),
            !showBigPlayButton && playing && !canPlay && (React.createElement(StyledSpinner, { size: SpinnerSizes.medium, type: SpinnerTypes.inverted })))),
        (((_d = (_c = videoPlayerRef.current) === null || _c === void 0 ? void 0 : _c.textTracks) === null || _d === void 0 ? void 0 : _d.length) || 0) > 0 && (React.createElement(VideoPlayerSubtitles, { selectedSubtitle: selectedSubtitle, textTracks: (_e = videoPlayerRef.current) === null || _e === void 0 ? void 0 : _e.textTracks, controlsVisible: controlsVisible, visible: selectedSubtitle !== -1 && !nativeVideoPlayerVisible, videoPlayerDimensions: videoPlayerDimensions })),
        controls && (React.createElement(VideoPlayerControls, { "data-type": "videoPlayerControls", visible: controlsVisible, sizeVariant: sizeVariant, backButtonLabel: backButtonLabel, menu: menu, onShowControls: restartHideControlsTimer, subtitleLabel: subtitleLabel, subtitleOffLabel: subtitleOffLabel, ariaLabels: ariaLabels, onMouseEnter: handleMouseEnterControls, onMouseLeave: handleMouseLeaveControls }))));
});
VideoPlayerWithoutContext.displayName = 'VideoPlayerWithoutContext';
const VideoPlayerContainer = styled.div(({ theme, fullscreen, showCursor, canPlay, nativeVideoPlayerVisible }) => css(css `
      width: ${canPlay && !fullscreen ? 'auto' : '100%'};
      position: relative;
      line-height: 0;
      align-items: center;
      justify-content: center;
      display: flex;
      overflow: hidden;
      cursor: ${showCursor ? 'default' : 'none'};
      background: ${theme.videoPlayer.backgroundColor};

      /* ensure no blue highlighted overlay is placed on top of the video */
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    `, 
// hide subtitles but with mode:hidden it will not trigger VTTCue events, so hide them with CSS
!nativeVideoPlayerVisible &&
    css `
        video::cue {
          opacity: 0;
          background: transparent;
        }

        /* works in safari & firefox. Needs to be duplicate, otherwise pseudo queries don't work */

        video::-webkit-media-text-track-container {
          opacity: 0;
        }

        video::-moz-media-text-track-display-backdrop {
          opacity: 0;
        }

        video::-webkit-media-text-track-display-backdrop {
          opacity: 0;
        }
      `));
const StyledVideo = styled.video(({ hasBeenSuspended }) => css([
    css `
      position: relative;
      flex: 1;
      z-index: 1;
      padding: 0;
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      outline: none;

      &::-internal-media-controls-overlay-cast-button {
        display: none;
      }
    `,
    !hasBeenSuspended &&
        css `
        /* Google Chrome has rounding issues when scaling the video. This can result in a
    1px black border at the bottom of the player. Upscaling the video .5% prevents this.
    We only apply this transform when the video has not been in suspended state, otherwise
    it will result in a black box on iOS.
    */
        transform: scale(1.005);
      `,
]));
const ClickToPlay = styled.div `
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  inset-block-end: 0;
  cursor: pointer;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (hover: hover) {
    &:hover {
      button:first-of-type {
        transform: scale(0.9);
      }
    }
  }
`;
const PlayIcon = styled(Icon)(({ theme, skeleton = false }) => css(css `
      color: ${skeleton
    ? theme.videoPlayer.initialPlayButton.skeleton.color
    : theme.videoPlayer.initialPlayButton.color};

      z-index: 150;

      &:hover {
        color: ${theme.videoPlayer.initialPlayButton.color};
      }
    `, skeleton &&
    css `
        pointer-events: none;
      `));
const BigPlayButton = styled.button(({ theme, skeleton = false }) => css(css `
      cursor: pointer;
      transition: ${createCSSTransition('transform', MotionEasings.easeOut, MotionDurations.fast)};
      position: absolute;
      background-color: ${skeleton
    ? theme.videoPlayer.initialPlayButton.skeleton.backgroundColor
    : theme.videoPlayer.initialPlayButton.backgroundColor};
      border-radius: 50%;
      width: ${theme.videoPlayer.initialPlayButton.size};
      height: ${theme.videoPlayer.initialPlayButton.size};
      border-width: 0;
      outline: none;
      display: flex;
      justify-content: center;
      align-items: center;
      touch-action: manipulation;
    `, skeleton &&
    css `
        pointer-events: none;
      `));
const TitleContainer = styled.div(({ theme, sizeVariant }) => css `
    position: absolute;
    inset-block-start: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    z-index: 5;
    padding: ${theme.videoPlayer.padding[sizeVariant]};
  `);
const TitleBackground = styled.div(({ theme, hide = false }) => css `
    background-image: linear-gradient(
      to top,
      ${theme.videoPlayer.title.backgroundColor.from},
      ${theme.videoPlayer.title.backgroundColor.to}
    );
    pointer-events: none;
    position: absolute;
    inset-block-start: 0;
    inset-block-end: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    z-index: -1;
    opacity: ${hide ? 0 : 1};
    transition: ${createCSSTransition('opacity', MotionEasings.default, MotionDurations.slow)};
  `);
const TitleContent = styled.div `
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-grow: 1;
`;
/* padding: compensate for the font-size difference between the text & the font-size (18px) of the icons (24px)  */
const VideoPlayerTitle = styled(Paragraph)(({ theme, visible }) => css `
    color: ${theme.videoPlayer.title.color};
    flex-grow: 1;
    height: 1.25rem;
    opacity: ${visible ? 1 : 0};
    pointer-events: ${visible ? 'auto' : 'none'};
    transform: ${visible ? 'none' : `translate(0, -${Spacings.large})`};
    transition: ${createCSSTransition('opacity', MotionEasings.default, MotionDurations.slow)},
      ${createCSSTransition('transform', MotionEasings.default, MotionDurations.slow)};
  `);
const StyledSpinner = styled(Spinner) `
  pointer-events: none;
  position: absolute;
  z-index: 2;
  inset-block-start: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
`;
const ActionButtonContainer = styled.div(({ visible, animate }) => css `
    display: flex;
    flex-direction: row;
    z-index: 6;
    opacity: ${visible ? 1 : 0};
    transform: ${visible ? 'none' : `translate(0, -${Spacings.large})`};
    transition: ${createCSSTransition('opacity', MotionEasings.default, animate ? MotionDurations.slow : 0)},
      ${createCSSTransition('transform', MotionEasings.default, animate ? MotionDurations.slow : 0)};
  `);
const VideoPlayerEndSlot = forwardRef(({ children, ...props }, ref) => {
    const [backgroundColorEnabled, setBackgroundColorEnabled] = useState(false);
    useEffect(() => {
        // timeout is needed, because the end-screen changes a lot of components visually
        // therefore the animation is not shown
        // Framer Motion is not used, because only the background-color (which resides in the theme)
        // changes and want to minimize the amount of renders for the VideoPlayer component
        setTimeout(() => {
            setBackgroundColorEnabled(true);
        }, 50);
    }, []);
    return (React.createElement(VideoPlayerEndContainer, { ...props, backgroundColorEnabled: backgroundColorEnabled, ref: ref },
        React.createElement(VideoPlayerEndSlotContent, null, children)));
});
VideoPlayerEndSlot.displayName = 'VideoPlayerEndSlot';
const VideoPlayerEndSlotContent = styled.div `
  position: relative;
  width: 100%;
  height: 100%;
`;
const VideoPlayerEndContainer = styled('div', shouldNotForwardProps(false, 'backgroundColorEnabled', 'emptyCanvas'))(({ theme, backgroundColorEnabled, emptyCanvas }) => css(css `
        position: absolute;
        inset-block-start: 0;
        inset-inline-start: 0;
        inset-inline-end: 0;
        inset-block-end: 0;
        line-height: ${LineHeights.small};
        z-index: 1;
        padding: ${emptyCanvas ? Spacings.medium : Spacings.xxxLarge}
          ${theme.videoPlayer.padding.mobile} ${createCustomSpacing(72 / 16)}
          ${theme.videoPlayer.padding.mobile};
        transition: ${createCSSTransition('background-color', MotionEasings.default, MotionDurations.xSlow)};

        ${MediaQueries.tablet} {
          padding: ${emptyCanvas ? Spacings.medium : createCustomSpacing(72 / 16)}
            ${theme.videoPlayer.padding.tablet} ${createCustomSpacing(72 / 16)}
            ${theme.videoPlayer.padding.tablet};
        }

        ${MediaQueries.desktop} {
          padding: ${emptyCanvas ? Spacings.xxLarge : createCustomSpacing(120 / 16)}
            ${theme.videoPlayer.padding.desktop} ${createCustomSpacing(120 / 16)}
            ${theme.videoPlayer.padding.desktop};
        }
      `, backgroundColorEnabled &&
    css `
          background-color: ${theme.videoPlayer.endScreen.backgroundColor};
        `));
